import { DOCTOR_MODAL_TYPE, useModalStore } from '@modal'
import { ColumnDef } from '@tanstack/react-table'
import { Table2 } from '@ui'
import { useMemo } from 'react'
import { palette } from '../../../../../themes/palette'
import { LabOrderData, LabOrderIncludedResource } from '../../../../../types/lab.type'
import { patientHistoryTableClassName } from './tableStyles'

type Props = {
  records: LabOrderData[]
  included?: LabOrderIncludedResource[]
  isLoading?: boolean
}

export default function LaboratoryResultsTable({ records, included = [], isLoading }: Props) {
  const { openModal } = useModalStore()

  const getLabTest = (order: LabOrderData) => {
    if (!order) return { lab: '-', test: '-' }
    const names = order.attributes.test_names
    return { lab: '-', test: names?.length ? names.join(', ') : '-' }
  }

  const columns = useMemo<ColumnDef<LabOrderData>[]>(() => {
    return [
      {
        accessorKey: 'test',
        header: 'Test',
        cell: ({ row }) => <span>{getLabTest(row.original).test}</span>
      },
      {
        accessorKey: 'lab',
        header: 'Lab',
        cell: ({ row }) => <span>{getLabTest(row.original).lab}</span>
      },
      {
        accessorKey: 'date',
        header: 'Date',
        cell: ({ row }) => (
          <span>
            {row.original.attributes.created_at
              ? new Date(row.original.attributes.created_at).toLocaleDateString()
              : '-'}
          </span>
        )
      },
      // interpretation removed for patient lab orders table
      {
        accessorKey: 'view',
        header: '',
        cell: ({ row }) => {
          const order = row.original
          const labResultsRefs = order.relationships?.lab_results?.data || []
          const includedArray = included as LabOrderIncludedResource[]

          if (labResultsRefs.length === 0) {
            return <span className="text-gray-400">-</span>
          }

          return (
            <div className="flex flex-col gap-2">
              {labResultsRefs.map(ref => {
                const includedLabResult = includedArray.find(i => i.type === 'lab_result' && i.id === ref.id)
                const resultName = (includedLabResult as any)?.attributes?.document?.name || 'View result'

                return (
                  <span
                    key={ref.id}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      e.preventDefault()
                      openModal(DOCTOR_MODAL_TYPE.LAB_RESULT_DETAILS, {
                        defaultData: (includedLabResult as any) || undefined,
                        orderId: order.id,
                        resultId: ref.id,
                        included: includedArray // Pass the included array here
                      })
                    }}
                    style={{
                      cursor: 'pointer',
                      fontWeight: 500,
                      textDecoration: 'underline',
                      color: palette.primary,
                      fontSize: '13px'
                    }}
                  >
                    {resultName}
                  </span>
                )
              })}
            </div>
          )
        }
      }
    ]
  }, [included])

  return (
    <div>
      <Table2
        className={patientHistoryTableClassName}
        columns={columns}
        data={records}
        emptyDescription="No results found."
        isLoading={isLoading}
        message="No results found"
        showPagination={false}
        wrapperClass="overflow-x-auto"
      />
    </div>
  )
}
